import { useNavigate } from 'react-router-dom';
import Button from '../components/button/Button';
import Card from '../components/card/Card';
import Input from '../components/input/Input';
import P from '../components/texts/p/P';
import { useFormik } from 'formik';
import { useState } from 'react';
import { login } from '../services/LoginService';
import { loginData } from '../types/interfaces';
import H from '../components/texts/h/H';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import Spinner from '../components/Spinner/spinner';
import { routes } from '../routes/routePaths';
import { useClientLogo } from '../hooks/useClientLogo';

interface ExtendedJwtPayload extends JwtPayload {
  realm_access?: {
    roles?: string[];
  };
  email?: string;
  name?: string;
  given_name?: string;
}

const Login = () => {
  const navigate = useNavigate();
  const [errorMsg, setErrorMsg] = useState('');
  const [isLoading, setIsloading] = useState<boolean>(false);
  const { logoUrl } = useClientLogo();
  
  const validate = (values: loginData) => {
    const errors: any = {};
    if (!values.user) {
      errors.user = 'El campo usuario es obligatorio';
    }
    if (!values.password) {
      errors.password = 'El campo contraseña es obligatorio';
    }
    return errors;
  };

  const formik = useFormik({
    initialValues: {
      user: '',
      password: '',
    },
    validate,
    onSubmit: (values) => {
      setIsloading(true);
      login(values).then((response) => {
        if (response.status === 200) {
          let decodedToken: ExtendedJwtPayload = jwtDecode(response.data.token);
          if (decodedToken) {
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('refresh_token', response.data.refreshToken);
            localStorage.setItem('user', values.user);
            localStorage.setItem(
              'roles',
              decodedToken.realm_access?.roles?.join(',') ?? ''
            );
            localStorage.setItem('email', decodedToken.email ?? '');
            localStorage.setItem('full_name', decodedToken.name ?? '');
            localStorage.setItem('name', decodedToken.given_name ?? '');
            localStorage.setItem('config', JSON.stringify(response.data.configuration ?? {}));
            const roles = decodedToken.realm_access?.roles ?? [];
            if (roles.includes('PRODUCTOR')) {
              navigate(routes.dashboard);
            } else navigate(routes.dashboardComercial);

            setIsloading(false);

            window.location.reload();
          }
        } else {
          setIsloading(false);
          setErrorMsg(response.message);
        }
      });
    },
  });

  return (
    <Card
      variant='primary'
      borderRadius={false}
      modifier='regular'
      display='flex'
      justifyContent='center'
      height='100vh'
      width='100%'
      alignItems='center'
    >
      <Card
        boxShadow={'1'}
        spacing={{ top: '6', right: '4', bottom: '4', left: '4' }}
        width='360px'
        variant='neutral'
        height='auto'
        margin='auto'
        modifier='inverted'
      >
        <Card
          display='flex'
          justifyContent='center'
          margin={{ top: '6', bottom: '3' }}
        >
          <img 
            src={logoUrl || 'assets/logos/logo.svg'} 
            width='64px' 
            alt="Logo"
          />
        </Card>
        <H
          withMargin={false}
          variant='primary'
          type='3'
          weight='bolder'
          modifier='strong'
          align='center'
        >
          Iniciar sesión
        </H>
        <form onSubmit={formik.handleSubmit}>
          <Card margin={{ top: '2' }}>
            <Card margin={{ bottom: '3' }}>
              <Input
                id='login-input-user'
                inputStatus='normal'
                label='Usuario'
                placeholder='Ingresá tu nombre de usuario'
                size='md'
                type='text'
                onChange={(e) => formik.setFieldValue('user', e.target.value)}
              />
              {formik.touched.user && formik.errors.user ? (
                <P variant='danger' withMargin={false} type='2'>
                  {formik.errors.user}
                </P>
              ) : null}
            </Card>
            <Input
              id='login-input-password'
              inputStatus='normal'
              label='Contraseña'
              placeholder='Ingresá tu contraseña'
              size='md'
              type='password'
              showPasswordButton
              onChange={(e) => formik.setFieldValue('password', e.target.value)}
            />
            {formik.touched.password && formik.errors.password ? (
              <P variant='danger' withMargin={false} type='2'>
                {formik.errors.password}
              </P>
            ) : null}
            {errorMsg !== '' && (
              <P variant='danger' type='1' weight='bold' align='center'>
                {errorMsg}
              </P>
            )}
            <Card
              display='flex'
              justifyContent='center'
              margin={{ top: '6', bottom: '3' }}
            >
              <Button
                type='submit'
                id='login-button-send'
                size='xl'
                fullWidth
                disabled={isLoading}
              >
                {isLoading ? (
                  <Spinner size='sm' variant='primary' />
                ) : (
                  'Iniciar sesión'
                )}
              </Button>
            </Card>
          </Card>
        </form>
      </Card>
    </Card>
  );
};

export default Login;
